// A paywall opening: one display of the sign-up app, started from one
// touchpoint. Every analytics event of the opening carries its touchpoint and
// entry type. The opening is kept in sessionStorage so that the return from
// Stripe Checkout can restore it.

export interface Opening {
  touchpoint: string
  paywall_entry?: "anonymous" | "signed_in"
  opening_id: string
  started_at: number
}

const STORAGE_KEY = "signup-opening"
const MAX_AGE_MS = 24 * 60 * 60 * 1000

function newOpeningId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

function save(opening: Opening): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(opening))
  } catch {
    // Storage can be unavailable (private mode, quota); attribution is best effort.
  }
}

function load(): Partial<Opening> | null {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null")
  } catch {
    return null
  }
}

export function startOpening(touchpoint: string | undefined, loggedIn: boolean): Opening {
  const opening: Opening = {
    touchpoint: touchpoint || "unknown",
    paywall_entry: loggedIn ? "signed_in" : "anonymous",
    opening_id: newOpeningId(),
    started_at: Date.now(),
  }
  save(opening)
  return opening
}

// The opening that led to a Stripe Checkout, restored on the return from
// Stripe when it is less than a day old.
export function restoreOpening(): Opening {
  const stored = load()
  if (
    stored &&
    typeof stored.touchpoint === "string" &&
    typeof stored.opening_id === "string" &&
    typeof stored.started_at === "number" &&
    Date.now() - stored.started_at < MAX_AGE_MS
  ) {
    return stored as Opening
  }
  return { touchpoint: "unknown", opening_id: newOpeningId(), started_at: Date.now() }
}

export function openingParams(opening?: Opening | null): Record<string, string | undefined> {
  if (!opening) return {}
  return { touchpoint: opening.touchpoint, paywall_entry: opening.paywall_entry }
}
